import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { TranslatePipe } from "@ngx-translate/core";
import { CommonDataService } from "../../../../../services/common-data.service";
import { CommonFunctionsService } from "../../../../../services/common-functions.service";
import { EstimateStatus } from "../../../../../models/dtos";
import { IEstimateStatisticsFormGroup } from "../../../../../models/properties/IEstimateStatisticsFormGroup";
import { MultilingualDatePipe } from "../../../../../pipes/multilingual-date-pipe/multilingual-date-pipe";

@Component({
    selector: 'app-sol-properties-estimate-statistics',
    templateUrl: './sol-properties-estimate-statistics.component.html',
    styleUrls: ['./sol-properties-estimate-statistics.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        TranslatePipe,
        MultilingualDatePipe,
    ]
})
export class SolPropertiesEstimateStatisticsComponent implements OnInit {
    estimateStatisticsForm!:FormGroup<IEstimateStatisticsFormGroup>;
    private destroyRef = inject(DestroyRef);

    constructor(private readonly fb: NonNullableFormBuilder,
        private readonly commonFunctionService: CommonFunctionsService,
        private readonly commonDataService: CommonDataService
    ) { }
    ngOnInit(): void {
        this.initializeForm();
        this.setupReadOnlyMode();
        this.setupLastSyncDateBinding();
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                if (isReadOnly) {
                    this.estimateStatisticsForm.disable({ emitEvent: false });
                } else {
                    this.estimateStatisticsForm.enable({ emitEvent: false });
                }
            });
    }

    private initializeForm(): void {
        let openResponse = this.commonDataService.getEstimateOpenResponse();
        let estimateData = openResponse.estimateData;
        let opportunityData = openResponse.opportunityRelationshipData;
        this.estimateStatisticsForm = this.fb.group<IEstimateStatisticsFormGroup>({
            status: this.fb.control<string>(this.commonFunctionService.getEstimateStatusName(estimateData.estimateStatusId)),
            submitted: this.fb.control<string>(this.getEstimateStatus(estimateData.estimateStatusId)),
            createdBy: this.fb.control<string>(estimateData.author),
            creationDate: this.fb.control<Date>(estimateData.createdDate),
            modifiedDate: this.fb.control<Date>(estimateData.modifiedDate),
            modifiedBy: this.fb.control<string>(estimateData.modifiedBy),
            lastUploadedToServer: this.fb.control<Date>(estimateData.lastUploadDate),
            lastDataSynchronization: this.fb.control<Date>(null),
            lastProposalGenerated: this.fb.control<Date>(estimateData.lastProposalGeneratedDate),
            proposalId: this.fb.control<string>(estimateData.proposalID),
            proposalLink: this.fb.control<string>(estimateData.proposalLink),
            opportunityId: this.fb.control<string>(opportunityData?.opportunityID ?? CONSTANTS.EMPTY_STRING), 
            opportunityLink: this.fb.control<string>(opportunityData?.opportunityLink ?? CONSTANTS.EMPTY_STRING)
        });
    }

    private setupLastSyncDateBinding(): void {
        this.commonDataService.lastSyncDate$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((lastSyncDate) => {
                this.estimateStatisticsForm.controls.lastDataSynchronization.setValue(lastSyncDate, { emitEvent: false });
            });
    }

    private getEstimateStatus(statusId: number): string {
        return statusId === EstimateStatus.Submitted ? CONSTANTS.Y : CONSTANTS.NA
    }

    get opportunityId(): string {
        return this.estimateStatisticsForm.controls.opportunityId?.value ?? CONSTANTS.EMPTY_STRING;
    }

    get opportunityLink(): string {
        return this.estimateStatisticsForm.controls.opportunityLink?.value ?? CONSTANTS.EMPTY_STRING;
    }

    get proposalLink(): string {
        return this.estimateStatisticsForm.controls.proposalLink?.value ?? CONSTANTS.EMPTY_STRING;
    }

    get proposalId(): string {
        return this.estimateStatisticsForm.controls.proposalId?.value ?? CONSTANTS.EMPTY_STRING;
    }
}